import { useCallback, useMemo } from 'react'
import moment from 'moment-timezone'

import ConfirmationDialog from 'app/common/components/Confirmation/ConfirmationDialog'
import PlainLink from 'app/common/ui-next/plain-link'
import PlainButtonAsLink from 'app/common/ui-next/plain-button-as-link'
import RestrictionsSettingsModal from 'app/components/common/RestrictionsSettingsModal'
import history from 'app/history'
import { replaceLast, replaceLastN } from 'app/common/lib/path'
import Modal from 'app/common/components/Modal'
import { useParams } from 'react-router'

import { WithRightsForOrg } from 'app/rights/RightsForOrg'
import * as rootRights from 'app/common/constants/rights/rootRights'
import * as specificRights from 'app/common/constants/rights/specificRights'
import SimpleDate from 'app/common/classes/simple-date'
import { OrgId } from 'app/dataTypes/org'
import { OrgComplexId } from 'app/dataTypes/org/complexes'
import { DietFoodTypeId } from 'app/dataTypes/org/dietFoodTypes'
import type { ComplexLastChange } from 'app/common/types/food/schools/complexes/history'

import PlainLinkTooltiped from './PlainLinkTooltiped'
import RefreshOrders from './RefreshOrders'
import CopyKit from '../../CopyKit'
import Dishes2MenuForm from './Dishes2MenuForm'
import Kit2MenuForm from './Kit2MenuForm'
import AdditionalSettingsModal from './AdditionalSettingsModal'
import { ValidityByDate } from '../../../commonComponents/ValidityByDate'
import { UpdatePurchasesLink } from './UpdatePurchasesLink'
import { ComplexHistoryLink } from '../../ComplexHistoryLink'

import styles from './RightBarContent.module.scss'

const menuRights = [rootRights.MENU, specificRights.MENU] as const
const complexesRigths = [rootRights.COMPLEXES, specificRights.COMPLEXES] as const
const complexesCompositionRigths = [
  rootRights.COMPLEXES,
  specificRights.COMPLEXES_COMPOSITION,
] as const

type Props = {
  id: number
  title: string
  orgID: string
  buildingId?: number
  dietFoodTypeId?: DietFoodTypeId
  date: string
  noDishes: boolean
  subsidyOrders: boolean
  deletable: boolean
  fixedPriceEnabled: boolean
  activeAction: string | null
  hideDishes2Menu?: boolean
  hideKit2Menu?: boolean
  hideSettings?: boolean
  deleteKit: () => Promise<void>
  hideKit: () => Promise<void>
  buffetZone: boolean
  hotMealsZone: boolean
  denyParentMoneySales: boolean
  denyMoreThanOneSales: boolean
  disableForTerminalGuestSales: boolean
  useAsAdditional: boolean
  fundMovementTypeCode?: number | null
  lastChange?: ComplexLastChange
}

const RightBarContent = ({
  id,
  title,
  orgID,
  buildingId,
  dietFoodTypeId,
  date,
  noDishes,
  subsidyOrders,
  deletable,
  fixedPriceEnabled,
  activeAction,
  hideDishes2Menu,
  hideKit2Menu,
  hideSettings,
  deleteKit,
  hideKit,
  buffetZone,
  hotMealsZone,
  denyParentMoneySales,
  denyMoreThanOneSales,
  disableForTerminalGuestSales,
  useAsAdditional,
  fundMovementTypeCode,
  lastChange,
}: Props) => {
  const { dietFoodTypeId: dietFoodTypeIdParam } = useParams<{ dietFoodTypeId?: string }>()
  const dietFoodTypePath = dietFoodTypeIdParam ? `/${dietFoodTypeIdParam}` : ''
  const { pathname } = window.location
  const pathnameWithoutDietFoodType = dietFoodTypeIdParam ? replaceLast(pathname) : pathname
  const basePath = activeAction
    ? replaceLastN(pathnameWithoutDietFoodType, 2)
    : replaceLast(pathnameWithoutDietFoodType)
  const returnPath = `${basePath}/current${dietFoodTypePath}`

  const handleReturnFromAction = useCallback(() => {
    history.push(returnPath)
  }, [returnPath])

  const simpleDate = useMemo(() => new SimpleDate(date, 'YYYY-MM-DD'), [date])

  return (
    <WithRightsForOrg pathTo={complexesRigths} readAffect="hide" editAffect="hide">
      <ValidityByDate affect="hide">
        <div>
          <ul className={styles.root}>
            {!hideDishes2Menu && (
              <li>
                <WithRightsForOrg pathTo={menuRights} editAffect="hide">
                  <PlainLinkTooltiped
                    to={`${basePath}/dishes2menu/${id}${dietFoodTypePath}`}
                    disabled={noDishes || activeAction === 'dishes2menu'}
                  >
                    {'Добавить эту продукцию в меню'}
                  </PlainLinkTooltiped>
                </WithRightsForOrg>
              </li>
            )}

            {!hideKit2Menu && (
              <li>
                <WithRightsForOrg pathTo={menuRights} editAffect="hide">
                  <PlainLinkTooltiped
                    to={`${basePath}/kit2menu/${id}${dietFoodTypePath}`}
                    disabled={noDishes || activeAction === 'kit2menu'}
                  >
                    {'Добавить комплект в меню'}
                  </PlainLinkTooltiped>
                </WithRightsForOrg>
              </li>
            )}

            <li>
              <PlainLinkTooltiped
                to={`${basePath}/copyKit/${id}${dietFoodTypePath}`}
                disabled={noDishes || activeAction === 'copyKit'}
              >
                {'Копировать комплект'}
              </PlainLinkTooltiped>
            </li>

            {/* Скрытие */}
            {noDishes && subsidyOrders && (
              <li>
                <ConfirmationDialog
                  onConfirm={hideKit}
                  text={`Вы уверены, что хотите переместить комплект «${title}» в архив?`}
                  confirmButtonText="Переместить в архив"
                  confirmButtonProps={{ warning: true }}
                >
                  {(handleConfirm) => (
                    <PlainButtonAsLink onClick={handleConfirm}>
                      {'Переместить в архив'}
                    </PlainButtonAsLink>
                  )}
                </ConfirmationDialog>
              </li>
            )}

            {/* Удаление link */}
            {noDishes && deletable && !subsidyOrders && (
              <li>
                <ConfirmationDialog
                  onConfirm={deleteKit}
                  text={`Вы уверены, что хотите переместить комплект «${title}» в архив?`}
                  confirmButtonText="Переместить в архив"
                  confirmButtonProps={{ error: true }}
                >
                  {(handleConfirm) => (
                    <PlainButtonAsLink onClick={handleConfirm}>
                      {'Переместить в архив'}
                    </PlainButtonAsLink>
                  )}
                </ConfirmationDialog>
              </li>
            )}

            {!noDishes && fixedPriceEnabled && (
              <li>
                <RefreshOrders
                  orgId={parseInt(orgID, 10)}
                  complexId={id}
                  date={date}
                  title={title}
                  message={`Обновить комплект «${title}» в уже созданных заявках на ${moment(date).format('DD.MM.YYYY')}?`}
                >
                  {(handleConfirmationRefreshOrders) => (
                    <PlainButtonAsLink onClick={handleConfirmationRefreshOrders}>
                      {'Обновить заявки'}
                    </PlainButtonAsLink>
                  )}
                </RefreshOrders>
              </li>
            )}

            {!noDishes && (
              <li>
                <UpdatePurchasesLink
                  date={simpleDate}
                  orgId={orgID as OrgId}
                  orgComplexId={String(id) as OrgComplexId}
                />
              </li>
            )}

            {!hideSettings && (
              <li>
                <PlainLink
                  to={`${basePath}/settings/${id}${dietFoodTypePath}`}
                  disabled={activeAction === 'settings'}
                >
                  {'Настройки'}
                </PlainLink>
              </li>
            )}

            {!hideSettings && (
              <AdditionalSettingsModal
                orgID={orgID}
                complexID={String(id)}
                buildingId={buildingId}
                dietFoodTypeId={dietFoodTypeId}
                buffetZone={buffetZone}
                hotMealsZone={hotMealsZone}
                denyParentMoneySales={denyParentMoneySales}
                denyMoreThanOneSales={denyMoreThanOneSales}
                disableForTerminalGuestSales={disableForTerminalGuestSales}
                useAsAdditional={useAsAdditional}
                fundMovementTypeCode={fundMovementTypeCode}
              >
                {(toggle) => (
                  <PlainButtonAsLink onClick={toggle}>
                    {'Дополнительные настройки'}
                  </PlainButtonAsLink>
                )}
              </AdditionalSettingsModal>
            )}
          </ul>

          {/* Добавление блюд комплекта в меню */}
          {activeAction === 'dishes2menu' && (
            <WithRightsForOrg pathTo={complexesCompositionRigths} editAffect="hide">
              <Modal close={returnPath}>
                <Dishes2MenuForm orgId={orgID} kitId={id} date={date} returnTo2={returnPath} />
              </Modal>
            </WithRightsForOrg>
          )}

          {/* Добавление комплекта в меню */}
          {activeAction === 'kit2menu' && (
            <WithRightsForOrg pathTo={complexesCompositionRigths} editAffect="hide">
              <Modal close={returnPath}>
                <Kit2MenuForm
                  orgId={parseInt(orgID, 10)}
                  kitId={id}
                  date={date}
                  returnTo2={returnPath}
                />
              </Modal>
            </WithRightsForOrg>
          )}

          {/* Копирование комплекта */}
          {activeAction === 'copyKit' && (
            <CopyKit
              orgID={orgID}
              kitID={String(id)}
              buildingId={buildingId}
              header={`Копирование комплекта «${title}»`}
              date={date}
              fixedPriceEnabled={fixedPriceEnabled}
              onClose={handleReturnFromAction}
            />
          )}

          {/* Настройки комплекта */}
          {activeAction === 'settings' && (
            <RestrictionsSettingsModal
              header="Настройки"
              headPhrase={`Для кого используется комплект «${title}»`}
              orgId={parseInt(orgID, 10)}
              complexId={id}
              onClose={handleReturnFromAction}
            />
          )}

          <ComplexHistoryLink
            className={styles.complexHistoryLink}
            orgId={orgID}
            complexId={id}
            complexTitle={title}
            lastChange={lastChange}
          />
        </div>
      </ValidityByDate>
    </WithRightsForOrg>
  )
}

export default RightBarContent
